import { inject, injectable } from 'inversify';
import { Observable, ReplaySubject } from 'rxjs';
import { take, tap, timeout } from 'rxjs/operators';

import { withRequestIdleCallback } from '../annotations/request-idle-callback.annotation';
import { BrowserUtils } from '../ui-utils/browser-utils';
import { OUTPUT_EXT_FROM_OPTIONS_FN, OUTPUT_VIDEO_ENCODER_FROM_OPTIONS_FN, TIME_VALIDATE_FN, UPLOADED_FILE_TARGET_SIZE_BY_DIMENSION_FN, UPLOADED_FILE_TARGET_SIZE_FN } from '../utils/options.utils';
import { UserSettings } from '../utils/request.utils';
import { SettingsService } from './settings.service';

declare var window;

@injectable()
export class WindowService {

    private windowClicked = true;
    get clicked(): boolean {
        return this.windowClicked;
    }

    private windowMouseMoved = false;
    get mousemoved(): boolean {
        return this.windowMouseMoved;
    }

    private windowMouseDowned = false;
    get mousedowned(): boolean {
        return this.windowMouseDowned;
    }

    private _userEngagedTriggered = false;
    private _userEngaged = new ReplaySubject<void>(1);
    get userEngaged() {
        return this._userEngaged.asObservable();
    }

    private _userClickedTriggered = false;
    private _userClicked = new ReplaySubject<void>(1);
    get userClicked() {
        return this._userClicked.asObservable();
    }

    private _onResize = new ReplaySubject<void>(1);
    get onResize() {
        return this._onResize.asObservable();
    }

    private _onScroll = new ReplaySubject<Event>(1);
    get onScroll() {
        return this._onScroll.asObservable();
    }

    private _onFileSelectBtnClicked = new ReplaySubject<{ dispatchedAt: Date }>(1);
    get onFileSelectBtnClicked() {
        return this._onFileSelectBtnClicked.asObservable();
    }

    public fileSelectBtnClicked() {
        this._onFileSelectBtnClicked.next({ dispatchedAt: new Date() });
    }

    constructor(
        @inject(SettingsService) private settingsService: SettingsService,
    ) {
        if (BrowserUtils.hasWindow()) {
            window['TIME_VALIDATE_FN'] = TIME_VALIDATE_FN;
            window['OUTPUT_EXT_FROM_OPTIONS_FN'] = OUTPUT_EXT_FROM_OPTIONS_FN;
            window['OUTPUT_VIDEO_ENCODER_FROM_OPTIONS_FN'] = OUTPUT_VIDEO_ENCODER_FROM_OPTIONS_FN;
            window['UPLOADED_FILE_TARGET_SIZE_FN'] = UPLOADED_FILE_TARGET_SIZE_FN;
            window['UPLOADED_FILE_TARGET_SIZE_BY_DIMENSION_FN'] = UPLOADED_FILE_TARGET_SIZE_BY_DIMENSION_FN;
        }
    }

    public initialize(): Observable<UserSettings> {
        this.registerWindowEvents();
        return this.settingsService.userSettings.pipe(
            take(1),
            timeout(4000)
        ).pipe(tap({
            next: (userSettings) => {
                if (userSettings.resolveMousemoveEventsStartup) {
                    this.updateUserEngagedState();
                }
            },
            error: () => {
                // not important
            },
        }));
    }

    private registerWindowEvents() {
        // if (isPlatformBrowser(this.platformId)) {
        this.setWindowClickListener();
        this.setWindowMouseMoveListener();
        this.setWindowMouseDownListener();
        this.setWindowResizeListener();
        this.setWindowScrollListener();
        // }
    }

    setWindowMouseMoveListener() {
        const mousemoveEventListener = () => {
            this.windowMouseMoved = true;
            window.removeEventListener('mousemove', mousemoveEventListener);
            window.removeEventListener('touchmove', mousemoveEventListener);
            this.updateUserEngagedState();
        };
        window.addEventListener('mousemove', mousemoveEventListener, { passive: true });
        window.addEventListener('touchmove', mousemoveEventListener, { passive: true });
    }

    setWindowMouseDownListener() {
        const mousedownEventListener = () => {
            this.windowMouseDowned = true;
            window.removeEventListener('mousedown', mousedownEventListener);
            window.removeEventListener('touchstart', mousedownEventListener);
            this.updateUserEngagedState();
        };
        window.addEventListener('mousedown', mousedownEventListener, { passive: true });
        window.addEventListener('touchstart', mousedownEventListener, { passive: true });
    }

    setWindowClickListener() {
        const clickEventListener = () => {
            this.windowClicked = true;
            window.removeEventListener('click', clickEventListener);
            window.removeEventListener('touchend', clickEventListener);
            this.updateUserEngagedState();
            this.updateUserClickedState();
        };
        window.addEventListener('click', clickEventListener);
        window.addEventListener('touchend', clickEventListener);
    }

    setWindowResizeListener() {
        const resizeEventListener = (e) => {
            this._onResize.next(e)
        };
        window.addEventListener('resize', resizeEventListener);
    }

    setWindowScrollListener() {
        const scrollEventListener = (e) => {
            this._onScroll.next(e)
        };
        window.addEventListener('scroll', scrollEventListener);
    }

    runOnWindow(fn: (window) => any): Window {
        try {
            return fn(window || {});
        } catch (e) {
            console.error('Error executing window cmd', e)
        }
        return null;
    }

    @withRequestIdleCallback
    private updateUserEngagedState() {
        if (!this._userEngagedTriggered) {
            this._userEngagedTriggered = true;
            this._userEngaged.next();
            this._userEngaged.complete();
        }
    }

    @withRequestIdleCallback
    private updateUserClickedState() {
        if (!this._userClickedTriggered) {
            this._userClickedTriggered = true;
            this._userClicked.next();
            this._userClicked.complete();
        }
    }

    @withRequestIdleCallback
    manuallyActivateTriggers() {
        this.updateUserEngagedState();
        this.updateUserClickedState();
    }
}
